import type { DeviceKind, DeviceState, EquipmentItem } from '../types';

const END_EFFECTOR_KINDS: DeviceKind[] = [
  'OnRobot3FG15',
  'OnRobot2FG7',
  'OnRobot2FG14',
  'NoGripper',
  'OnRobotDualQuickChanger',
  'OnRobotScrewdriver',
  'OnRobotVGP20',
  'CustomGripper',
  'SchunkEGx',
  'OnRobotHexE',
  'Robotiq2F',
  'RobotiqEPick',
];

const FORCE_SENSOR_KINDS: DeviceKind[] = ['OnRobotHexE'];

const WELD_MACHINE_KINDS: DeviceKind[] = [
  'WeldMachine',
  'MillerWeldMachine',
  'EsabWeldMachine',
];

/**
 * DH gripper device kinds that are no longer supported.
 * Kept as string literals (not DeviceKind) because these types
 * have been removed from the DeviceConfiguration union.
 */
export const DEPRECATED_DH_KINDS = [
  'DHAG105145',
  'DHPGC30060',
  'DHCGI100170',
] as const;

export const NETWORK_DEVICE: DeviceKind[] = ['ModbusTCPServer'];

export function isEndEffector(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return END_EFFECTOR_KINDS.includes(kind);
}

export function isActuatingGripper(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return isEndEffector(kind) && !FORCE_SENSOR_KINDS.includes(kind);
}

/**
 * Reads a device's connectivity flag, for the device kinds that report one.
 *
 * Returns `undefined` when the kind has no connectivity concept at all, which
 * callers must keep distinct from `false`: a device that never reports
 * connectivity is not a disconnected device.
 */
export function getGripperConnectivity(
  state: DeviceState | undefined,
): boolean | undefined {
  if (!state || typeof state !== 'object' || !('isConnected' in state)) {
    return undefined;
  }

  return typeof state.isConnected === 'boolean' ? state.isConnected : undefined;
}

export function isForceSensor(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return FORCE_SENSOR_KINDS.includes(kind);
}

export function hasEndEffector(equipment: EquipmentItem[]): boolean {
  return equipment.some((device) => isEndEffector(device.config.kind));
}

export function isDynamicBase(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return kind === 'EwellixLiftTLT';
}

export function isWeldMachine(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return WELD_MACHINE_KINDS.includes(kind);
}

export function isMountingBracket(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return kind === 'MountingBracket';
}

export function isMountingAdapter(value: DeviceKind | { kind: DeviceKind }) {
  const kind = typeof value === 'string' ? value : value.kind;

  return kind === 'MountingAdapter';
}

export function hasMountingBracket(equipment: EquipmentItem[]): boolean {
  return equipment.some((device) => isMountingBracket(device.config.kind));
}

/**
 * Normalize an equipment name for loose comparisons.
 * - lowercases
 * - trims spaces
 * - strips a trailing duplicate suffix like " (2)"
 */
export function normalizeEquipmentName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s*\(\d+\)\s*$/, '')
    .trim();
}

/**
 * Loosely check if a displayed equipment name refers to a given preset label.
 * Uses normalizeEquipmentName() and checks for startsWith to allow duplicates.
 */
export function isNameLike(displayName: string, presetName: string): boolean {
  const a = normalizeEquipmentName(displayName);
  const b = normalizeEquipmentName(presetName);

  return a.startsWith(b);
}
